import { Injectable } from '@angular/core';
import { Article } from '@ca/core/models';
import { ArticleService } from '@ca/core/services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as actions from './articles.actions';

@Injectable()
export class ArticlesEffects {
    loadAdminArticles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.loadAdminArticles),
            switchMap(() =>
                this.articleService.getArticles().pipe(
                    map((resp: Article[]) => actions.loadAdminArticlesSuccess({ articles: resp.sort((a1, a2) => this.sortByOrder(a1, a2)) || [] })),
                    catchError(() => {
                        return of(actions.loadAdminArticlesFailed({ error: this.translateService.instant('ARTICLES.LOAD_ARTICLES_ERROR') }));
                    }),
                ),
            ),
        ),
    );

    loadArticles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.loadArticles),
            switchMap(() =>
                this.articleService.getArticles().pipe(
                    map((resp: Article[]) =>
                        actions.loadArticlesSuccess({ articles: resp.filter(x => x.enabled).sort((a1, a2) => this.sortByOrder(a1, a2)) || [] }),
                    ),
                    catchError(() => {
                        return of(actions.loadArticlesFailed({ error: this.translateService.instant('ARTICLES.LOAD_ARTICLES_ERROR') }));
                    }),
                ),
            ),
        ),
    );

    removeArticle$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.deleteArticle),
            switchMap(action =>
                this.articleService.removeArticle(action.id).pipe(
                    map(id => actions.deleteArticleSuccess({ id: id })),
                    catchError(() => {
                        return of(actions.deleteArticleFailed({ error: this.translateService.instant('ARTICLES.DELETE_ERROR') }));
                    }),
                ),
            ),
        ),
    );

    createArticle$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.createArticle),
            switchMap(action => {
                return this.articleService.createArticle(action.article).pipe(
                    map((resp: Article) => actions.createArticleSuccess({ article: resp })),
                    catchError(() => {
                        return of(actions.createArticleFailed({ error: this.translateService.instant('ARTICLES.CREATE_ERROR') }));
                    }),
                );
            }),
        ),
    );

    updateArticle$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.updateArticle),
            switchMap(action => {
                return this.articleService.updateArticle(action.article).pipe(
                    map((resp: Article) => actions.updateArticleSuccess({ article: resp })),
                    catchError(() => {
                        return of(actions.updateArticleFailed({ error: this.translateService.instant('ARTICLES.UPDATE_ERROR') }));
                    }),
                );
            }),
        ),
    );

    getArticle$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.getArticle),
            switchMap(action => {
                return this.articleService.getArticleById(action.id).pipe(
                    map((resp: Article) => actions.getArticleSuccess({ article: resp })),
                    catchError(() => {
                        return of(actions.getArticleFailed({ error: this.translateService.instant('ARTICLES.GET_ARTICLE_ERROR') }));
                    }),
                );
            }),
        ),
    );

    constructor(
        private actions$: Actions,
        private articleService: ArticleService,
        private translateService: TranslateService,
    ) {}

    private sortByOrder(a1: Article, a2: Article): number {
        if (a1.order > a2.order) {
            return 1;
        } else if (a1.order < a2.order) {
            return -1;
        }
        return 0;
    }
}
